import {
  Resource_AppointmentTaskResultType,
  Resource_AppointmentTaskStatus,
  useGetDocumentLazyQuery,
  useUpdateAppointmentTaskResultMutation,
} from '@/generated/graphql';
import {
  CheckCircleOutlined,
  EditOutlined,
  EyeOutlined,
  StopOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { AppointmentTaskComponent } from '@components/appointment-details/appointment-tasks/types';
import { AveliosButton } from '@components/common-layout/avelios-button';
import { FilePreview } from '@components/FilePreview';
import {
  Drawer,
  message,
  Popconfirm,
  Result,
  Space,
  Spin,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import { RcFile } from 'antd/es/upload';
import { UploadRequestOption } from 'rc-upload/es/interface';
import { FC, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import uuid from 'react-uuid';
import { documentToUploadFile } from '../../../../helpers/document/document-to-upload-file';
import { bytesToMB, proxy_uploadDocument } from '../../../../helpers/document/document-upload';
import { proxy_buildDocumentDownloadUrl } from '../../../../helpers/document/document-url-helper';
import { SUPPORTED_DOCUMENT_MIME_TYPES } from '../../../../helpers/document/supported-document-mime-types';
import { DocumentUploadType } from '../../../../helpers/document/types';
import { MimeType, mimeTypeForString } from '../../../../helpers/file/mime-types';
import { useCurrentUser } from '../../../../helpers/utils';
import { ScreenSizeContext } from '../../../../pages/_app';

const { Dragger } = Upload;

const staticProps: UploadProps = {
  accept: SUPPORTED_DOCUMENT_MIME_TYPES.join(', '),
  maxCount: 1,
  multiple: true,
  listType: 'picture',
};

export const DocumentUploadAppointmentTask: FC<AppointmentTaskComponent> = ({
  proxyAppointmentTask,
  updateAppointmentTaskStatus,
}) => {
  const { formatMessage } = useIntl();

  const { isMobile } = useContext(ScreenSizeContext);

  const { currentUser, userLoading } = useCurrentUser();

  const appointmentTask = proxyAppointmentTask.appointmentTask;
  const appointmentTaskId = appointmentTask?.id;

  const clinicId = proxyAppointmentTask?.clinicId;

  const resultReferences = appointmentTask?.resultReferences;

  const taskStatus =
    appointmentTask?.status ?? Resource_AppointmentTaskStatus.AppointmentTaskStatusNone;
  const taskCompleted = taskStatus === Resource_AppointmentTaskStatus.AppointmentTaskStatusFinished;
  const taskUnavailable =
    taskStatus === Resource_AppointmentTaskStatus.AppointmentTaskStatusResolvedUnavailable;
  const taskResolved = taskCompleted || taskUnavailable;
  const canRejectTask = !!appointmentTask?.appointmentRequestId;

  const uploadedDocumentId = resultReferences?.find(
    (reference) =>
      reference?.type === Resource_AppointmentTaskResultType.AppointmentTaskResultTypeDocument,
  )?.referenceId;

  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const [getDocumentQuery, { loading: getDocumentLoading }] = useGetDocumentLazyQuery();

  const getDocument = (documentId: Nullable<string>) => {
    if (!clinicId || !documentId) {
      console.warn('get document: missing props');
      return undefined;
    }

    return getDocumentQuery({
      variables: {
        input: {
          documentId: uploadedDocumentId,
          clinicId,
        },
      },
    });
  };

  useEffect(() => {
    getDocument(uploadedDocumentId)?.then((result) => {
      const uploadDocument = documentToUploadFile(result.data?.getDocument?.document);

      setFileList([uploadDocument]);
    });
  }, [clinicId]);

  const [updateAppointmentTaskResultMutation] = useUpdateAppointmentTaskResultMutation();

  const handleUploadComplete = (uploadedDocumentId: string) => {
    const portalUserId = currentUser?.id;

    if (!clinicId || !appointmentTaskId || !portalUserId) {
      console.log('Cannot update appointment task results. Parameters are missing.');
      return;
    }

    updateAppointmentTaskResultMutation({
      variables: {
        clinicId,
        appointmentTaskId,
        portalUserId,
        newResultReferences: {
          referenceId: uploadedDocumentId,
          type: Resource_AppointmentTaskResultType.AppointmentTaskResultTypeDocument,
        },
      },
    }).then((result) => {
      if (result.data?.updateAppointmentTaskResult?.appointmentTask?.id) {
        updateAppointmentTaskStatus(
          Resource_AppointmentTaskStatus.AppointmentTaskStatusStarted,
          false,
        );
      }
    });
  };

  const handleDocumentRemoval = (documentIdToRemove: string) => {
    const portalUserId = currentUser?.id;

    if (!clinicId || !appointmentTaskId || !portalUserId) {
      console.log('Cannot update appointment task results. Parameters are missing.');
      return;
    }

    updateAppointmentTaskResultMutation({
      variables: {
        clinicId,
        appointmentTaskId,
        portalUserId,
        newResultReferences: [],
      },
    }).then((result) => {
      if (!result?.errors) {
        updateAppointmentTaskStatus(
          Resource_AppointmentTaskStatus.AppointmentTaskStatusCreated,
          false,
        );
      }
    });
  };

  const [selectedDocument, setSelectedDocument] = useState<{
    url: string;
    mimeType: MimeType;
  }>();

  const beforeUpload = (file: RcFile, maxFileSizeBytes: Nullable<number>) => {
    if (!maxFileSizeBytes) {
      return true;
    }

    updateAppointmentTaskStatus(Resource_AppointmentTaskStatus.AppointmentTaskStatusStarted); // Non-blocking

    if (file.size > maxFileSizeBytes) {
      const maxFileSizeMegaBytes = bytesToMB(maxFileSizeBytes);
      message.error(
        formatMessage(
          { id: 'question.document.max-file-size-exceeded' },
          { fileName: file.name, maxFileSize: maxFileSizeMegaBytes },
        ),
      );
      return Upload.LIST_IGNORE; // Prevent the file from being added to the list
    }
    return true; // Allow the file to be added
  };

  const loading = userLoading || getDocumentLoading;

  const disabled =
    loading ||
    [
      Resource_AppointmentTaskStatus.AppointmentTaskStatusNone,

      Resource_AppointmentTaskStatus.AppointmentTaskStatusCanceled,
      Resource_AppointmentTaskStatus.AppointmentTaskStatusNotYetCreated,
    ].includes(taskStatus) ||
    taskResolved;

  const props: UploadProps = {
    ...staticProps,
    disabled,
    beforeUpload: (file) => beforeUpload(file, undefined), // set max file size here once implemented
    fileList,
    onChange(info) {
      let newFileList = [...info.fileList];

      newFileList = newFileList.slice(-1);

      newFileList = newFileList.map((file) => {
        if (file.response) {
          // Component will show file.url as link
          file.url = file.response.url;
        }
        return file;
      });

      setFileList(newFileList);

      const { status, name } = info.file;
      if (status !== 'uploading') {
        console.log(info.file, info.fileList);
      }
      if (status === 'done') {
        message.info(formatMessage({ id: 'question.document.upload-success' }, { fileName: name }));
      }
    },
    onRemove(file) {
      if (file.status === 'done') {
        handleDocumentRemoval(file.uid);

        message.info(
          formatMessage({ id: 'question.document.file-deleted' }, { fileName: file.name }),
        );
      }
    },
    onPreview: (file) => {
      const mimeType = mimeTypeForString(file.type);
      if (file.url && mimeType) {
        setSelectedDocument({ url: file.url, mimeType });
      } else {
        console.error(
          'Could not open preview. File url or mime type missing / invalid',
          file.url,
          mimeType,
        );
      }
    },
    customRequest: (options: UploadRequestOption) => {
      const fileToUpload = options.file;

      if (clinicId && appointmentTaskId) {
        proxy_uploadDocument(
          clinicId,
          fileToUpload,
          { type: DocumentUploadType.appointmentTask, referenceId: appointmentTaskId },
          undefined,
          {
            onSuccess: (response) => {
              const documentId = response.data.id;

              if (documentId) {
                const uploadedFile: UploadFile = {
                  uid: documentId,
                  fileName: options.filename,
                  name: options.filename ?? uuid(),
                  status: 'done',
                  url: proxy_buildDocumentDownloadUrl(
                    clinicId,
                    documentId,
                    false,
                    false,
                  )?.toString(),
                };

                handleUploadComplete(documentId);
                options.onSuccess?.(uploadedFile, response.request);
              } else {
                options.onError?.(
                  {
                    name: 'error',
                    message: formatMessage({ id: 'question.document.upload-error' }),
                  },
                  options.file,
                );
              }
            },
            onVirusFound: () => {
              const errorMessage = formatMessage({ id: 'question.document.upload-error.virus-found' });
              message.error(errorMessage);
              options.onError?.({ name: 'error', message: errorMessage }, options.file);
            },
            onError: (error) => {
              if (error?.response?.status === 413) {
                const errorMessage = formatMessage({ id: 'question.document.upload-error.file-too-large' });
                message.error(errorMessage);
                options.onError?.({ name: 'error', message: errorMessage }, options.file);
                return;
              }

              const errorMessage = formatMessage(
                { id: 'question.document.upload-error' },
                { fileName: options.filename },
              );
              message.error(errorMessage);
              options.onError?.({ name: 'error', message: errorMessage }, options.file);
            },
          },
        );
      }
    },
    showUploadList: {
      showPreviewIcon: true,
      previewIcon: <EyeOutlined />,
      showRemoveIcon: !disabled,
    },
  };

  if (!appointmentTask) {
    return (
      <Result
        status="warning"
        title={formatMessage({ id: 'error.title' })}
        subTitle={formatMessage({ id: 'error.message' })}
      />
    );
  }

  return (
    <Spin spinning={loading}>
      <Space style={{ width: '100%' }} direction={'vertical'}>
        <Dragger {...props}>
          <p className="ant-upload-drag-icon">
            {!taskCompleted ? (
              !uploadedDocumentId ? (
                <UploadOutlined />
              ) : (
                <EditOutlined />
              )
            ) : (
              <CheckCircleOutlined />
            )}
          </p>
          {!taskCompleted ? (
            <>
              <p className="ant-upload-text">
                {!uploadedDocumentId
                  ? formatMessage({ id: 'appointment-task.document-upload.dragger.title' })
                  : formatMessage({ id: 'appointment-task.document-upload.dragger.title.edit' })}
              </p>
              <p className="ant-upload-hint">
                {formatMessage({ id: 'question.document.upload.description' })}
                <br />
                {formatMessage({ id: 'question.document.upload.max-file-size' })}
                <br />
                {formatMessage({ id: 'question.document.upload.allowed-formats' })}
              </p>
            </>
          ) : (
            <p className={'ant-upload-hint'}>
              {formatMessage({ id: 'question.document.upload.description.complete' })}
            </p>
          )}
        </Dragger>
        <Popconfirm
          title={formatMessage({ id: 'question.document.upload.confirm-save' })}
          onConfirm={() =>
            updateAppointmentTaskStatus(
              Resource_AppointmentTaskStatus.AppointmentTaskStatusFinished,
            )
          }
        >
          <AveliosButton
            disabled={taskResolved || !uploadedDocumentId}
            style={taskCompleted ? { backgroundColor: 'darkgreen', color: 'white' } : undefined}
            type={'primary'}
            block
            icon={<CheckCircleOutlined />}
          >
            {taskCompleted
              ? formatMessage({ id: 'appointment-task.document-upload.complete' })
              : formatMessage({ id: 'action.confirm' })}
          </AveliosButton>
        </Popconfirm>
        {canRejectTask && (
          <Popconfirm
            title={formatMessage({
              id: 'appointment-task.document-upload.not-available.confirm',
            })}
            onConfirm={() =>
              updateAppointmentTaskStatus(
                Resource_AppointmentTaskStatus.AppointmentTaskStatusResolvedUnavailable,
              )
            }
          >
            <AveliosButton disabled={taskResolved} block icon={<StopOutlined />}>
              {taskUnavailable
                ? formatMessage({ id: 'appointment-task.document-upload.not-available.done' })
                : formatMessage({ id: 'appointment-task.document-upload.not-available' })}
            </AveliosButton>
          </Popconfirm>
        )}
      </Space>
      {selectedDocument && (
        <Drawer
          width={isMobile ? '100%' : '50%'}
          open={!!selectedDocument}
          onClose={() => {
            setSelectedDocument(undefined);
          }}
        >
          <FilePreview
            requiresAuthentication
            fileUrl={selectedDocument.url}
            mimeType={selectedDocument.mimeType}
          />
        </Drawer>
      )}
    </Spin>
  );
};
